import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  inject,
  Input,
  ViewChild,
  AfterViewInit,
  TemplateRef,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { SubsectionService } from '../../services/subsection.service';
import { Subject, takeUntil } from 'rxjs';
import {
  TextEditorSelectPrivateCondition,
  TextEditorSelectPrivateConditionProps,
} from '../../interfaces/ui-task.interface';
import { EmitterService } from '../../services/emitter.service';
import { AnnotationService } from '../../services/annotation.service';
import { ActiveSubsectionState, MalwareScanningStatus, SimpleFilePage, SimpleFilePages } from '../../interfaces';
import { ProjectService } from '../../services/project.service';
import { MediaPlayerFile } from '../../interfaces/media-player-store.interface';
import { AuthService } from '../../services/auth.service';
import { PermissionService } from '../../services/permissions.service';
import { AnnotationEventSubscriptionService } from './services/annotation-event-subscription.service';
import { AssetUploadService } from './services/asset-upload.service';
import { SubsectionEventService } from './services/subsection-event.service';
import { MediaPlayerHelperService } from './services/media-player-helper.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../services/media-player-store.service';
import { FileUtilsService } from '../../services/file-utils.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MediaType } from '../../models/media-player-types';

@Component({
  selector: 'old-mtm-media-player',
  templateUrl: './old-media-player.component.html',
  styleUrl: './old-media-player.component.scss',
  standalone: false,
})
export class OldMediaPlayerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  constructor(
    private readonly elementRef: ElementRef,
    public readonly activeModal: NgbActiveModal
  ) {}

  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly annotationService: AnnotationService = inject(AnnotationService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly assetUploadService: AssetUploadService = inject(AssetUploadService);
  private readonly annotationEventSubscriptionService: AnnotationEventSubscriptionService = inject(
    AnnotationEventSubscriptionService
  );

  private readonly subsectionEventService: SubsectionEventService = inject(SubsectionEventService);
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly helperService: MediaPlayerHelperService = inject(MediaPlayerHelperService);
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);
  private readonly destroy$: Subject<void> = new Subject<void>();

  // Asset list and info templates
  @ViewChild('assetListTemplateRef', { static: true }) assetListTemplateRef!: TemplateRef<any>;
  @ViewChild('assetInfoTemplateRef', { static: true }) assetInfoTemplateRef!: TemplateRef<any>;

  @Output() switchVersionClick: any = new EventEmitter<Partial<ActiveSubsectionState>>();

  // Component behavior inputs
  @Input() item: MediaPlayerFile | null = null;
  @Input() projectId: string | null = null;
  @Input() sectionId: string | null = null;
  @Input() subsectionId: string | null = null;
  @Input() typeId: string | null = null;
  @Input() enablePage: boolean = false;
  @Input() filePages: SimpleFilePages | null = null;
  @Input() activePage: SimpleFilePage | null = null;
  @Input() showCloseButton: boolean = false;
  @Input() editingEnabled: boolean = true;
  @Input() enableResponsive: boolean = false;
  @Input() enableAnnotation: boolean = false;
  @Input() parentElementContainer: string | null = null;
  @Input() commentOnly: boolean = false;
  @Input() respondToTaskQueue: boolean = false;
  @Input() isKpfPreview: boolean = false;
  @Input() previewContainerHeight: string | null = null;
  @Input() isAssetListVisible: boolean = false;
  @Input() isAssetInfoVisible: boolean = false;

  // Display inputs
  @Input() width: string = '400';
  @Input() height: string = '300';
  @Input() mode: string | null = null;
  @Input() miniPlayer: boolean = false;
  @Input() autoPlayVideo: boolean = false;
  @Input() showDownloadButtonIfNotPreviewAble: boolean = false;
  @Input() portfolioPreview: boolean = false;
  @Input() videoPreloadMethod: any = null;
  @Input() audioPreloadMethod: string = 'metadata';
  @Input() ignoreOldItem: boolean = false;
  @Input() instanceId!: string;

  // instanceId: string = Math.random().toString(36).substring(2, 15);
  mpStore: MediaPlayerStore;
  private pendingChanges: SimpleChanges = {};

  get annotationPlugin(): any {
    return this.mpStore.annotationPluginSignal();
  }

  get mediaType(): string | null {
    return this.mpStore.mediaTypeSignal() ?? null;
  }

  get hasEditPermission(): boolean {
    return this.mpStore.hasEditPermissionSignal() ?? false;
  }

  get isBusy(): boolean {
    return this.mpStore.isBusySignal() ?? false;
  }

  get isMediaPlayerLoaded(): boolean {
    return this.mpStore?.isMediaPlayerLoadedSignal() ?? true;
  }

  private _projectParticipants: any[] = [];
  get projectParticipants(): any[] {
    return this._projectParticipants;
  }
  @Input()
  set projectParticipants(value: any[]) {
    const participants: any[] = value?.length ? value : this.projectService.project?.participants;
    this._projectParticipants = participants || [];
  }

  protected readonly MalwareScanningStatus: typeof MalwareScanningStatus = MalwareScanningStatus;
  readonly MediaType = MediaType;
  displayInfo: any = {};

  ngOnChanges(changes: SimpleChanges): void {
    this.pendingChanges = { ...this.pendingChanges, ...changes };

    if (!this.mpStore) {
      return;
    }

    this.processInputChanges(this.pendingChanges);
    this.pendingChanges = {};
  }

  ngOnInit(): void {
    this.mpStore = this.mpStoreService.createStore(this.instanceId);
    // Initialize store
    this.initializeStore();
    this.processInputChanges(this.pendingChanges);
    this.pendingChanges = {};
    document.body.classList.add('media-preview-page-active');
    this.updateViewportHeight();
    this.checkPermission();
    if (this.respondToTaskQueue) {
      this.handleTaskQueueEvents();
    }
  }

  private processInputChanges(changes: SimpleChanges): void {
    if (!this.mpStore || !changes || !Object.keys(changes).length) {
      return;
    }

    let isCurrentAssetChanged: boolean = false;

    if (changes.item) {
      this.mpStore?.updateStore({ imagePristine: true });
    }

    if (changes.item?.currentValue) {
      this.mpStore?.updateStore({ oldItem: false });
      this.assetUploadService.resetAndReinitialize(this.item, this.enablePage, this.filePages, false, this.instanceId);
      isCurrentAssetChanged = true;

      const ext: string = this.fileUtilsService.getFileExtension(this.item?.name);
      if (ext) {
        this.displayInfo.extension = ext;
      }
    }
    if (changes.activePage?.currentValue) {
      this.mpStore?.updateStore({ activePage: this.activePage });

      if (!changes.activePage.isFirstChange() || !changes.activePage.previousValue) {
        this.assetUploadService.resetAndReinitialize(this.item, this.enablePage, this.filePages, true, this.instanceId);
      }
      isCurrentAssetChanged = true;
    }

    // Update state when inputs change
    this.updateStoreFromChanges(changes);

    if (isCurrentAssetChanged) {
      this.mpStore?.updateStore({ isAssetInfoVisible: false });
    }
  }

  ngAfterViewInit(): void {
    // Subscribe to subsection state changes
    this.subsectionEventService.init(this.instanceId);
    // Wire annotation service <-> plugin events
    this.annotationEventSubscriptionService.init(this.instanceId);
  }

  ngOnDestroy(): void {
    this.mpStoreService.disposeStore(this.instanceId);
    setTimeout(() => {
      const mediaElements: any = this.elementRef.nativeElement.querySelectorAll('[src]');
      mediaElements.forEach((element: any) => {
        if (element.nodeName === 'VIDEO') {
          element.pause();
          element.removeAttribute('src');
          element.load();
        } else {
          element.removeAttribute('src');
        }
      });
    }, 350);

    document.body.classList.remove('media-preview-page-active');
    document.documentElement.style.removeProperty('--vh');
    this.destroy$.next();
    this.destroy$.complete();
    EmitterService.get('MEDIA_PLAYER:FULLSCREEN').emit(false);
  }

  @HostListener('window:resize')
  public updateViewportHeight(): void {
    this.helperService.updateViewportHeight();
  }

  private initializeStore(): void {
    const userInfo: any = this.authService.getAuthUser();
    const { subsectionState } = this.subsectionService;

    // Determine projectId, sectionId, subsectionId from item or inputs
    if (!this.projectId) {
      if (this.item?.projectId) {
        this.projectId = this.item.projectId;
      }
    }

    if (!this.sectionId) {
      if (this.item?.sectionId) {
        this.sectionId = this.item.sectionId;
      }
    }

    if (!this.subsectionId) {
      if (this.item?.subSectionId) {
        this.subsectionId = this.item.subSectionId;
      }
    }

    // Initialize all state in mpStore
    this.mpStore.updateStore({
      instanceId: this.instanceId,
      projectId: this.projectId,
      sectionId: this.sectionId,
      subsectionId: this.subsectionId,
      typeId: this.typeId,
      item: this.item,
      projectParticipants: this.projectParticipants,
      showDownloadButtonIfNotPreviewAble: this.showDownloadButtonIfNotPreviewAble,
      portfolioPreview: this.portfolioPreview,
      respondToTaskQueue: this.respondToTaskQueue,
      enableResponsive: this.enableResponsive,
      enableAnnotation: this.enableAnnotation,
      parentElementContainer: this.parentElementContainer,
      activePage: this.activePage,
      commentOnly: this.commentOnly,
      hideAnnotationToolbar: false,
      oldItem: false,
      userInfo,
      subsectionState,
      videoPreloadMethod: this.videoPreloadMethod,
      isAssetListVisible: this.isAssetListVisible,
      isAssetInfoVisible: this.isAssetInfoVisible,
    });
  }

  private updateStoreFromChanges(changes: SimpleChanges): void {
    const store: MediaPlayerStore | null = this.mpStore;
    if (!store) return;

    const storeUpdateMap: Record<string, () => void> = {
      enableAnnotation: () => store.updateStore({ enableAnnotation: this.enableAnnotation }),
      enableResponsive: () => store.updateStore({ enableResponsive: this.enableResponsive }),
      parentElementContainer: () => store.updateStore({ parentElementContainer: this.parentElementContainer }),
      commentOnly: () => store.updateStore({ commentOnly: this.commentOnly }),
      respondToTaskQueue: () => store.updateStore({ respondToTaskQueue: this.respondToTaskQueue }),
      typeId: () => store.updateStore({ typeId: this.typeId }),
      projectParticipants: () => store.updateStore({ projectParticipants: this.projectParticipants }),
      videoPreloadMethod: () => store.updateStore({ videoPreloadMethod: this.videoPreloadMethod }),
      showDownloadButtonIfNotPreviewAble: () =>
        store.updateStore({ showDownloadButtonIfNotPreviewAble: this.showDownloadButtonIfNotPreviewAble }),
      portfolioPreview: () => store.updateStore({ portfolioPreview: this.portfolioPreview }),
      projectId: () => store.updateStore({ projectId: this.projectId }),
      sectionId: () => store.updateStore({ sectionId: this.sectionId }),
      subsectionId: () => store.updateStore({ subsectionId: this.subsectionId }),
      isAssetListVisible: () => store.updateStore({ isAssetListVisible: this.isAssetListVisible }),
      isAssetInfoVisible: () => store.updateStore({ isAssetInfoVisible: this.isAssetInfoVisible }),
    };

    Object.keys(storeUpdateMap).forEach((key: string) => {
      if (changes[key]) {
        storeUpdateMap[key]();
      }
    });
  }

  private checkPermission(): void {
    if (!this.mpStore) return;

    const projectId: string | undefined = this.projectService.project?.id;
    if (!projectId) {
      return;
    }
    const hasReviewLink: boolean = !!this.mpStore.subsectionStateSignal()?.reviewLink;
    const hasEditPermission: boolean =
      this.editingEnabled && this.permissionService.canUpdateProject() && !hasReviewLink;
    this.mpStore.updateStore({ hasEditPermission });
  }

  private handleTaskQueueEvents(): void {
    EmitterService.get(TextEditorSelectPrivateCondition)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: TextEditorSelectPrivateConditionProps) => {
          const { privateCondition, resolve } = data;
          if (this.mpStore.privateConditionSignal() == privateCondition) {
            resolve(true);
            return;
          }

          this.annotationService.updatePrivateCondition(privateCondition);
          resolve(true);
        },
      });
  }

  public handleAssetClick(): void {
    this.mpStore.updateStore({ isAssetListVisible: false });
  }

  public handleScanStatusChange(): void {
    if (this.item.malwareScanningStatus === MalwareScanningStatus.Clean) {
      this.assetUploadService.resetAndReinitialize(this.item, this.enablePage, this.filePages, false, this.instanceId);
    }
  }

  public switchVersion(versionNumber: number): void {
    this.subsectionEventService.switchVersion(
      versionNumber,
      this.mpStore.subsectionStateSignal()?.reviewLink ?? null,
      this.switchVersionClick
    );
  }

  public createNewVersion(): void {
    this.subsectionEventService.createNewVersion();
  }

  public showManageVersions(): void {
    this.subsectionEventService.showManageVersions();
  }

  public openReviewLinkSettings(): void {
    this.subsectionEventService.openReviewLinkSettings();
  }

  public toggleAssetList(): void {
    this.subsectionEventService.toggleAssetList();
  }

  public toggleAssetInfo(): void {
    this.subsectionEventService.toggleAssetInfo();
  }

  public downloadAsset(): void {
    this.subsectionEventService.downloadAsset();
  }
}
